import React, {useReducer, useState} from 'react';
import {ContentAdDesktop} from 'app/styles/content';
import {string} from 'prop-types';
import {Helmet} from 'react-helmet-async';
import {fetchLeagueStandings} from 'app/routes/league/LeaguePageActions';
import {LeagueStandingPageReducer, initialStandingData} from 'app/routes/league/LeaguePageReducer';
import StandingsHeader from 'app/components/standings/StandingsHeader';
import {
  FormInformation,
  getStandingsMetaInformation,
  StandingInformation,
} from 'app/components/standings/StandingInformation';
import {LeagueStanding} from 'app/components/standings/LeagueStanding';
import {useParams} from 'react-router';
import {ButtonGroup} from 'app/components/button/ButtonGroup';
import {useFetchData, useRestoreData} from 'app/hooks/dataHooks';
import {COLORS, LOADING_MESSAGE, LoadingIndicator, Typography} from '@fupa/fupa-uikit';
import {Subheading} from 'app/styles/heading';
import {LinkOnComponent} from 'app/components/links/link';
import styled from 'styled-components';
import TaboolaFeed from 'app/components/ads/taboola/TaboolaFeed';
import {TaboolaFeedConfig} from 'app/components/ads/taboola/TaboolaFeedConfig';

const AdditionalContent = styled.div`
  padding: 0 1rem;
`;

const allowedTypes = ['home', 'away', 'firstRound', 'secondRound', 'form'];
const LeagueStandingPage = ({defaultTitle, districtName, standingFormUpdatedAt, baseUrl, dispatchRedux}) => {
  const {restoredData} = useRestoreData('LeagueStandingPage');
  const {leagueSlug, seasonSlug, type} = useParams();
  const combinedSlug = `${leagueSlug}${seasonSlug}${type}`;
  const initialState = restoredData ?? initialStandingData;
  const [state, dispatch] = useReducer(LeagueStandingPageReducer, initialState);

  const loadData = table => {
    const filter = table ?? type;
    return fetchLeagueStandings(leagueSlug, seasonSlug, filter, standingFormUpdatedAt, dispatchRedux)(dispatch, state);
  };
  const key = type && allowedTypes.includes(type) ? type : 'total';
  const {isFetching, data} = state[key];
  const standings = key === 'form' && data.length ? data : data?.standings ?? [];
  const text = data.text ?? '';
  useFetchData(state, loadData, 'LeagueStandingPage', true);
  const [activeTable, setActiveTable] = useState(key);

  const handleChange = value => {
    loadData(value);
    setActiveTable(value);
  };

  const {title, meta} = getStandingsMetaInformation(key, defaultTitle, districtName);
  let standingBaseUrl = `${baseUrl}/standing`;

  const canonical =
    key !== 'total' && key !== 'form' ? <link rel='canonical' href={`https://www.fupa.net${standingBaseUrl}`} /> : null;

  const buttons = [
    {url: `${standingBaseUrl}`, label: 'Gesamt', value: 'total'},
    {url: `${standingBaseUrl}/home`, label: 'Heim', value: 'home'},
    {url: `${standingBaseUrl}/away`, label: 'Auswärts', value: 'away'},
    {url: `${standingBaseUrl}/firstRound`, label: 'Hin', value: 'firstRound'},
    {url: `${standingBaseUrl}/secondRound`, label: 'Rück', value: 'secondRound'},
    {url: `${standingBaseUrl}/form`, label: 'Form', value: 'form'},
  ];

  const content = isFetching ? (
    <LoadingIndicator message={LOADING_MESSAGE.loadingStandings} bgColor />
  ) : (
    <>
      <LeagueStanding view={activeTable} standings={standings} />
      {activeTable === 'form' ? <FormInformation /> : <StandingInformation helptext={text} standings={standings} />}
    </>
  );

  return (
    <ContentAdDesktop top='7.125rem' withMobileBanner contentPaddingBottom='1rem'>
      <Helmet title={`${title} - `} meta={meta}>
        {canonical}
      </Helmet>
      <ButtonGroup activeValue={activeTable} onChange={handleChange} buttons={buttons} />
      <StandingsHeader activeTable={activeTable} />
      {content}
      <Subheading variant='subheading2'>Weitere Inhalte</Subheading>
      <AdditionalContent>
        <Typography variant='caption1'>
          <LinkOnComponent
            color={COLORS.fupaPrimary}
            decoration={'underline'}
            to={`/league/${leagueSlug}/standing/alltime`}>
            &gt;&gt; Zur Ewigen Tabelle
          </LinkOnComponent>
        </Typography>
      </AdditionalContent>
      <TaboolaFeed key={combinedSlug} config={TaboolaFeedConfig.STANDINGS} />
    </ContentAdDesktop>
  );
};

LeagueStandingPage.propTypes = {
  defaultTitle: string.isRequired,
  districtName: string.isRequired,
};

export default LeagueStandingPage;
